import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
//import useLocalStorage from '../../hooks/useLocalStorage';
import { appMode } from '@utils/url';
import { TLoginRes } from '@api/auth.api';
import { createTransform } from 'redux-persist';

export interface User {
  token: string;
  sessionID: string;
  userID: string;
  email: string;
  position: string;
  name: string;
  shortName: string;
  birthdate: string;
  avatar: string;
  roleLevel: number;
  pass?: string;
  isServoAccount: boolean;
}

export interface ISystemDialog {
  _id?: number;
  message: string;
  dialog_type: 'Dialog Box' | 'Notification Message';
}

export interface IUserLogin {
  token: string;
  isMaintenanceMode: number;
  systemDialogs: ISystemDialog[];
  user: {
    name: string;
    email: string;
    id: string;
    shortName?: string;
    roleLevel?: number;
    isServoAccount?: boolean;
  };
  device: {
    verified: boolean;
    verificationCode: string;
    deviceID: string;
  };
  mfa?: {
    require: boolean;
    token: string;
  };
  sessionID: string;
  logInProcess: string;
  usersID?: string;
  userID?: string;
  verificationCode: string;
}

export interface UserState {
  info: User;
  isLoggedIn: boolean;
  isMFAEnabled: boolean;
  loginInfo: IUserLogin;
  mfaLogin: {
    open: boolean;
    mfaLoginToken: string;
    signature: string;
    loading: boolean;
    loginToken: string;
    data?: TLoginRes['data'];
  };
}

export const initialState: UserState = {
  info: {
    token: '',
    sessionID: '',
    userID: '',
    email: '',
    position: '',
    name: '',
    shortName: '',
    birthdate: moment(new Date()).format('YYYY-MM-DD'),
    avatar: '',
    pass: '',
    roleLevel: 0,
    isServoAccount: false,
  },
  isLoggedIn: false,
  isMFAEnabled: false,
  loginInfo: {
    token: '',
    isMaintenanceMode: 0,
    systemDialogs: [],
    user: {
      name: '',
      email: '',
      id: '',
    },
    device: {
      verified: false,
      verificationCode: '',
      deviceID: '',
    },
    sessionID: '',
    logInProcess: '',
    verificationCode: '',
  },
  mfaLogin: {
    open: false,
    mfaLoginToken: '',
    signature: '',
    loading: false,
    loginToken: '',
    data: undefined,
  },
};

export const saveUserThunk = createAsyncThunk(
  'user/saveUser',
  async (data: IUserLogin) => {
    // simulate a delay (like an API call)
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const isServoAccount =
      data?.user?.isServoAccount ??
      (appMode === 'local' &&
        window.location.origin === 'http://127.0.0.1:5173');

    return {
      token: data?.token || '',
      sessionID: data?.sessionID || '',
      userID: data?.userID || data?.user?.id || '',
      email: data?.user?.email || '',
      position: '',
      name: data?.user?.name || '',
      shortName: data?.user?.shortName || '',
      birthdate: moment(new Date()).format('YYYY-MM-DD'),
      avatar: '',
      roleLevel: data?.user?.roleLevel ?? 6,
      isServoAccount,
    };
  },
);

export const UserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.info = { ...state.info, ...action.payload };
      state.isLoggedIn = true;
    },
    setLoginInfo: (state, action: PayloadAction<IUserLogin>) => {
      state.loginInfo = action.payload;
    },
    removeLoginInfo: (state) => {
      state.loginInfo = {
        token: '',
        isMaintenanceMode: 0,
        systemDialogs: [],
        user: {
          name: '',
          email: '',
          id: '',
        },
        device: {
          verified: false,
          verificationCode: '',
          deviceID: '',
        },
        sessionID: '',
        logInProcess: '',
        verificationCode: '',
      };
    },
    removeUser: () => {
      return initialState;
    },
    setMFALogin: (
      state,
      action: PayloadAction<{
        mfaLoginToken: string;
        loginToken: string;
        data: TLoginRes['data'];
      }>,
    ) => {
      state.mfaLogin.open = true;
      state.mfaLogin.mfaLoginToken = action.payload.mfaLoginToken;
      state.mfaLogin.loginToken = action.payload.loginToken;
      state.mfaLogin.data = action.payload.data;
    },

    setMFAStatus: (state, action: PayloadAction<boolean>) => {
      state.isMFAEnabled = action.payload;
    },

    setMFALoginLoading: (state, action: PayloadAction<boolean>) => {
      state.mfaLogin.loading = action.payload;
    },

    clearMFALogin: (state) => {
      state.mfaLogin = {
        open: false,
        mfaLoginToken: '',
        signature: '',
        loading: false,
        loginToken: '',
        data: undefined,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveUserThunk.fulfilled, (state, action) => {
      state.info = { ...state.info, ...action.payload };
      state.isLoggedIn = true;
    });
  },
});

/**
 * Redux-persist transform for the `user` slice that sanitizes `mfaLogin` state
 * before it is persisted to storage and after it is rehydrated.
 *
 * Purpose:
 * MFA login is a multi-step, transient flow (token exchange, signature,
 * loading state, etc.). None of that should survive a page reload — if it
 * did, a stale `mfaLoginToken`/`signature`/`loginToken` could be rehydrated
 * into a fresh session and cause incorrect MFA state or stuck loading flags.
 *
 * Behavior:
 * - `open`, `mfaLoginToken`, `signature`, `loading`, `loginToken`, and `data`
 *   are all reset to their empty/default values.
 * - `isMFAEnabled` is preserved separately on the user slice,
 *   since whether the user has MFA enabled is not transient — it falls
 *   back to `false` only if it was never set.
 * - `whitelist: ['user']` scopes this transform to only run against the
 *   `user` slice, not the entire persisted root state.
 *
 * Registered in the redux-persist config's `transforms` array for the
 * `user` reducer.
 */
export const mfaLoginResetTransform = createTransform<UserState, UserState>(
  (inboundState) => ({
    ...inboundState,
    mfaLogin: {
      open: false,
      mfaLoginToken: '',
      signature: '',
      loading: false,
      loginToken: '',
      data: undefined,
    },
  }),
  (outboundState) => ({
    ...outboundState,
    isMFAEnabled:
      outboundState.isMFAEnabled ??
      (
        outboundState.mfaLogin as UserState['mfaLogin'] & {
          isMFAEnabled?: boolean;
        }
      )?.isMFAEnabled ??
      false,
    mfaLogin: {
      open: false,
      mfaLoginToken: '',
      signature: '',
      loading: false,
      loginToken: '',
      data: undefined,
    },
  }),
  { whitelist: ['user'] },
);

export default UserSlice.reducer;
export const {
  setUser,
  removeUser,
  setLoginInfo,
  removeLoginInfo,
  setMFALogin,
  setMFAStatus,
  setMFALoginLoading,
  clearMFALogin,
} = UserSlice.actions;
